/**
 * Custom Inquiry inline panel — escape hatch adjacent to Pay.
 *
 * Authenticated context: buyer is already logged in, contact is
 * prefilled from the auth profile. The buyer adds a free-text scope
 * description, optional timing, optional alternate phone, and
 * submits. Lands in `intake_lead` for operator follow-up.
 *
 * On success the panel swaps to a confirmation state with a back
 * link. The configurator state above the panel is preserved so the
 * buyer can return to Pay if they change their mind.
 */

import { useState } from "react";

import { useClientAuth } from "@/auth/client-auth-context";

import {
  ApiError,
  submitCustomInquiry,
  type CustomInquirySubmission,
} from "../api";

import "./custom-inquiry-panel.css";

interface ClientProfileShape {
  display_name: string;
  contact_name: string;
  contact_email: string | null;
  company: string | null;
}

interface Props {
  me: ClientProfileShape | null;
  attemptedCart: Array<{ value: string; kind: string }>;
  onClose: () => void;
}

type SubmissionState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok"; leadId: string }
  | { kind: "error"; message: string };

export function CustomInquiryPanel({ me, attemptedCart, onClose }: Props) {
  const { accessToken } = useClientAuth();
  const [scope, setScope] = useState("");
  const [urgency, setUrgency] = useState("");
  const [phone, setPhone] = useState("");
  const [submission, setSubmission] = useState<SubmissionState>({ kind: "idle" });

  const canSubmit =
    scope.trim().length > 0 && submission.kind !== "submitting";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmission({ kind: "submitting" });
    try {
      const payload: CustomInquirySubmission = {
        contact_name: me?.contact_name || "",
        contact_email: (me?.contact_email || "").toLowerCase(),
        contact_phone: phone.trim() || null,
        company: me?.company || null,
        scope_description: scope.trim(),
        urgency: urgency.trim() || null,
        attempted_cart: attemptedCart,
        source: "portal_commission_inline",
      };
      const result = await submitCustomInquiry(payload, accessToken);
      setSubmission({ kind: "ok", leadId: result.lead_id });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Couldn't submit your inquiry. Please try again or email us directly.";
      setSubmission({ kind: "error", message });
    }
  };

  if (submission.kind === "ok") {
    return (
      <div
        id="custom-inquiry-panel"
        className="custom-inquiry-panel custom-inquiry-panel-success"
        role="status"
        aria-live="polite"
      >
        <h3>Inquiry received.</h3>
        <p>
          Thanks — we'll be in touch within one business day. Reference{" "}
          <code>{submission.leadId.slice(0, 8)}</code> if you need to follow
          up.
        </p>
        <button type="button" onClick={onClose} className="custom-inquiry-panel-back">
          Back to the configurator
        </button>
      </div>
    );
  }

  return (
    <form
      id="custom-inquiry-panel"
      className="custom-inquiry-panel"
      onSubmit={handleSubmit}
      aria-labelledby="custom-inquiry-panel-heading"
    >
      <h3 id="custom-inquiry-panel-heading">Tell us what you're scoping</h3>
      <p className="custom-inquiry-panel-lede">
        Larger engagements, CIDR blocks, parent-company audits, or anything
        the configurator can't express. We'll come back with a quote.
      </p>

      <label className="custom-inquiry-panel-field">
        <span>
          Scope description
          <span aria-hidden="true" className="custom-inquiry-panel-required">
            *
          </span>
        </span>
        <textarea
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          rows={4}
          required
          placeholder="A parent with five brand subsidiaries, IP range 203.0.113.0/24, and we need the report tailored for ISO 27001 evidence."
        />
      </label>

      <label className="custom-inquiry-panel-field">
        <span>Timing</span>
        <input
          type="text"
          value={urgency}
          onChange={(e) => setUrgency(e.target.value)}
          placeholder="Need findings before our board meeting on March 20"
        />
      </label>

      <label className="custom-inquiry-panel-field">
        <span>Alternate phone</span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          placeholder="+1 555 123 4567"
        />
      </label>

      <div className="custom-inquiry-panel-actions">
        <button type="button" onClick={onClose} className="custom-inquiry-panel-cancel">
          Cancel
        </button>
        <button type="submit" className="custom-inquiry-panel-submit" disabled={!canSubmit}>
          {submission.kind === "submitting" ? "Sending…" : "Send inquiry"}
        </button>
      </div>

      {submission.kind === "error" && (
        <p className="custom-inquiry-panel-error" role="alert">
          {submission.message}
        </p>
      )}
    </form>
  );
}
